import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — Historique du chat IA client (persistance)
// Le fil de discussion de l'assistant IA d'un client est stocké dans
// le Core (core_conversations / core_conversation_messages). Il survit
// donc au rafraîchissement de la page et reste consultable côté marchand
// (Timeline + Communication du Socle central).
// ─────────────────────────────────────────────────────────────

const AI_ENTITY_TYPE = 'ai_client';

export interface ClientAiChatMessage {
  role: 'user' | 'assistant';
  content: string;
  results?: Record<string, unknown>[];
  /** Note vocale : référence durable de l'audio (réécoutable après refresh). */
  audio?: { url: string; duration: number };
}

export interface ClientAiConversation {
  conversationId: number | null;
  messages: ClientAiChatMessage[];
}

/** Charge le fil persistant du chat IA d'un client depuis le Core. */
export async function loadClientAiConversation(userId: number): Promise<ClientAiConversation> {
  try {
    if (!userId) return { conversationId: null, messages: [] };

    const { data: conv } = await supabase
      .from('core_conversations')
      .select('id')
      .eq('entity_type', AI_ENTITY_TYPE)
      .eq('entity_id', String(userId))
      .is('deleted_at', null)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    const conversationId = (conv?.id as number) ?? null;
    if (!conversationId) return { conversationId: null, messages: [] };

    const { data: rows } = await supabase
      .from('core_conversation_messages')
      .select('direction, author_type, body, attachments')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    const messages: ClientAiChatMessage[] = (rows || []).map((r) => {
      const row = r as {
        direction: string | null;
        author_type: string | null;
        body: string | null;
        attachments: unknown;
      };
      const isAssistant = row.author_type === 'ai' || row.direction === 'outbound';
      const atts = Array.isArray(row.attachments)
        ? (row.attachments as Record<string, unknown>[])
        : [];
      // Message assistant : les pièces jointes sont des résultats de recherche.
      // Message utilisateur : une note vocale éventuelle (`kind: 'voice_note'`).
      const voice = !isAssistant
        ? atts.find((a) => a && a.kind === 'voice_note' && typeof a.url === 'string')
        : undefined;
      const results = isAssistant ? atts : [];
      return {
        role: isAssistant ? 'assistant' : 'user',
        content: row.body || '',
        results: results.length > 0 ? results : undefined,
        audio: voice
          ? { url: String(voice.url), duration: Number(voice.duration) || 0 }
          : undefined,
      };
    });

    return { conversationId, messages };
  } catch {
    return { conversationId: null, messages: [] };
  }
}

/** Efface le fil persistant du chat IA d'un client (masquage logique). */
export async function clearClientAiConversation(userId: number): Promise<void> {
  try {
    const { data: conv } = await supabase
      .from('core_conversations')
      .select('id')
      .eq('entity_type', AI_ENTITY_TYPE)
      .eq('entity_id', String(userId))
      .is('deleted_at', null)
      .limit(1)
      .maybeSingle();
    const conversationId = (conv?.id as number) ?? null;
    if (!conversationId) return;
    await supabase
      .from('core_conversations')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', conversationId);
  } catch {
    /* best-effort */
  }
}

/** Retrouve l'id de la conversation IA d'un client donné (pour la rouvrir côté marchand). */
export async function findClientAiConversationId(clientId: number): Promise<number | null> {
  try {
    if (!clientId) return null;
    const { data } = await supabase
      .from('core_conversations')
      .select('id')
      .eq('entity_type', AI_ENTITY_TYPE)
      .eq('entity_id', String(clientId))
      .is('deleted_at', null)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    return (data?.id as number) ?? null;
  } catch {
    return null;
  }
}